import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, API, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { POSTCARD_IMG, LOGO_IMG } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { PostcardFace } from "@/components/PostcardFace";
import { PostcardPreview } from "@/components/PostcardPreview";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { Storefront, ChartLineUp, Robot, ArrowsClockwise, UploadSimple, CheckCircle, SignOut, Eye, Ticket, Camera, QrCode, Package, X, DownloadSimple, TrendUp, Receipt } from "@phosphor-icons/react";
import { Html5Qrcode } from "html5-qrcode";

const HERO = "https://images.pexels.com/photos/6205471/pexels-photo-6205471.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AI_IMG = "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzg2OTgwNDgzfDA&ixlib=rb-4.1.0&q=85";

export default function MerchantPortal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("slots");
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState("");
  const [slots, setSlots] = useState([]);
  const [face, setFace] = useState("Front");
  const [selected, setSelected] = useState([]);
  const [pricing, setPricing] = useState({ regional_discount_pct: 15, base_slot_price: 450 });

  const loadSlots = (cid) => api.get(`/campaigns/${cid}/slots`).then(({ data }) => setSlots(data));

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
    api.get("/campaigns").then(({ data }) => {
      setCampaigns(data);
      if (data.length) { setActiveCampaign(data[0].id); loadSlots(data[0].id); }
    });
  }, []);

  const changeCampaign = (cid) => { setActiveCampaign(cid); setSelected([]); loadSlots(cid); };

  const toggleSlot = (slot) => {
    setSelected((prev) => prev.includes(slot.id) ? prev.filter((id) => id !== slot.id) : [...prev, slot.id]);
  };

  const selectedSlots = slots.filter((s) => selected.includes(s.id));
  const total = selectedSlots.reduce((sum, s) => sum + s.price, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 p-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><img src={LOGO_IMG} alt="PMI" className="w-40 sm:w-[220px] h-auto" /></Link>
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <span className="text-sm text-slate-500 hidden sm:inline">{user.name}</span>
                <button onClick={logout} data-testid="merchant-logout" className="text-sm flex items-center gap-1 text-slate-500 hover:text-navy-900">
                  <SignOut size={16} /> Sign out
                </button>
              </>
            ) : (
              <Link to="/login" className="text-sm bg-navy-900 text-white px-4 py-2 hover:bg-emerald-700 transition-colors" data-testid="merchant-login-link">Partner Login</Link>
            )}
          </div>
        </div>
      </header>

      <div className="relative h-56 md:h-72 overflow-hidden">
        <img src={HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy-900/55" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full text-white">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-400">For Local Businesses</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-2 max-w-xl leading-tight">Claim your spot on the postcard</h1>
            <p className="mt-3 text-slate-200 max-w-lg">Reach every household in your target zip code. Track every view and redemption in real time.</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-6 relative z-10">
        <div className="inline-flex flex-wrap bg-white border border-slate-200 shadow-sm" data-testid="merchant-tabs">
          {[
            { id: "slots", label: "Reserve Slots", icon: Storefront },
            { id: "scan", label: "Scan to Redeem", icon: Camera },
            { id: "analytics", label: "My Analytics", icon: ChartLineUp },
            { id: "ai", label: "AI Services", icon: Robot },
            { id: "subs", label: "My Subscriptions", icon: Receipt },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-6 py-4 text-sm font-[600] flex items-center gap-2 transition-colors duration-200 ${
                tab === t.id ? "bg-navy-900 text-white" : "text-slate-500 hover:text-navy-900"}`}>
              <t.icon size={18} weight="duotone" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {tab === "slots" && <ZoneSlotsTab />}
        {tab === "scan" && <ScanTab />}
        {tab === "analytics" && <AnalyticsTab campaigns={campaigns} />}
        {tab === "ai" && <AiTab aiImg={AI_IMG} user={user} />}
        {tab === "subs" && <SubscriptionsTab user={user} />}
      </div>
      <Footer />
    </div>
  );
}

function ZoneSlotsTab() {
  const [sel, setSel] = useState({}); // categoryId -> { name, fullPrice }
  const [form, setForm] = useState({ business_name: "", contact_name: "", contact_email: "", contact_phone: "", offer_details: "" });
  const [logoId, setLogoId] = useState(null);
  const [couponId, setCouponId] = useState(null);
  const [uploading, setUploading] = useState("");
  const [cap, setCap] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);

  const [campaigns, setCampaigns] = useState([]);
  const [campId, setCampId] = useState("");
  const load = () => api.get("/mailer-campaigns").then(({ data }) => { setCampaigns(data); setCampId((c) => c || data[0]?.id || ""); }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 7000); return () => clearInterval(t); }, []);
  const [catList, setCatList] = useState([]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCatList(data)).catch(() => {}); }, []);

  const camp = campaigns.find((c) => c.id === campId) || campaigns[0];
  const zone = camp?.zone;
  const zip = camp?.primaryZip || "";
  const cats = zone?.categories || [];
  const inv = zone?.inventory;
  const catSig = cats.map((c) => `${c.categoryId}:${c.unavailable}:${c.fullAvailable}`).join("|");

  useEffect(() => {
    setSel((prev) => {
      const next = {};
      Object.entries(prev).forEach(([cid, v]) => {
        const c = cats.find((x) => x.categoryId === cid);
        if (c && !c.unavailable && c.fullAvailable) next[cid] = v;
      });
      return next;
    });
    // eslint-disable-next-line
  }, [campId, catSig]);

  const toggleFull = (c) => setSel((prev) => {
    const next = { ...prev };
    if (next[c.categoryId]) delete next[c.categoryId];
    else next[c.categoryId] = { name: c.name, fullPrice: c.fullPrice, businessCategory: "" };
    return next;
  });
  const setSelCategory = (cid, val) => setSel((prev) => ({ ...prev, [cid]: { ...prev[cid], businessCategory: val } }));

  const resetAll = () => {
    setSel({}); setLogoId(null); setCouponId(null); setCap("");
    setForm({ business_name: "", contact_name: "", contact_email: "", contact_phone: "", offer_details: "" });
    toast.success("Selections cleared");
  };

  const selList = Object.entries(sel).map(([categoryId, v]) => ({ categoryId, ...v }));
  const total = selList.reduce((s, x) => s + x.fullPrice, 0);

  const upload = async (file, setter, kind) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter(data.file_id); toast.success(`${kind} uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(""); }
  };

  const checkout = async () => {
    if (!selList.length) { toast.error("Click at least one open slot on the card"); return; }
    if (!form.business_name || !form.offer_details || !form.contact_email) { toast.error("Fill in business name, offer and email"); return; }
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout/zone-slots", {
        zipCode: zip,
        selections: selList.map((x) => ({ categoryId: x.categoryId, kind: "FULL", businessCategory: (x.businessCategory && x.businessCategory !== "__other__") ? x.businessCategory : null })),
        redemption_cap: Number(cap) || 0, logo_file_id: logoId, coupon_file_id: couponId,
        origin_url: window.location.origin, ...form,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setCheckingOut(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Live Availability · Visual Mailer</p>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-1">Claim your category</h2>
            {camp && <p className="text-sm text-slate-500 mt-1" data-testid="merchant-campaign-descriptor">{camp.descriptor}</p>}
          </div>
          <select value={campId} onChange={(e) => { setCampId(e.target.value); setSel({}); }} data-testid="zone-zip-select"
            className="border border-slate-300 px-4 py-2.5 bg-white focus:ring-2 focus:ring-navy-900 focus:outline-none max-w-[320px]">
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.descriptor}</option>)}
          </select>
        </div>

        {inv && (
          <div className="mb-5 bg-white border border-slate-200 p-3 flex items-center justify-between" data-testid="merchant-inventory">
            <p className="text-sm text-slate-500">Tap an open slot on the card below to reserve a <span className="font-[700] text-navy-900">Full Slot</span> (${cats[0]?.fullPrice ?? 500} each).</p>
            <p className="text-sm font-[700] text-emerald-700" data-testid="merchant-remaining">{inv.remainingFull} / {inv.capacityFull} full slots left</p>
          </div>
        )}

        {cats.length === 0
          ? <div className="bg-white border border-slate-200 p-10 text-center text-slate-400">No zones available yet.</div>
          : <PostcardPreview zone={zone} title="Your 9×12 Mailer · Tap to Reserve" interactive generic selectedIds={Object.keys(sel)} onSelect={toggleFull} />}
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-24 bg-white border border-slate-200 p-6 shadow-sm" data-testid="checkout-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl tracking-tight">Your Order</h3>
            {(selList.length > 0 || logoId || couponId) && (
              <button onClick={resetAll} data-testid="merchant-reset" className="text-xs font-[600] text-slate-400 hover:text-red-600 transition-colors">Clear all</button>
            )}
          </div>
          {selList.length === 0 ? (
            <p className="text-sm text-slate-400 mt-4">No spots selected yet. Tap an open slot on the card, then pick its business category.</p>
          ) : (
            <ul className="mt-4 space-y-3 text-sm" data-testid="order-list">
              {selList.map((x) => (
                <li key={x.categoryId} className="border-b border-slate-100 pb-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">{camp?.descriptor || zip} · Open Spot <em className="not-italic text-slate-400">(Full Slot)</em></span>
                    <span className="font-[600]">${x.fullPrice}</span>
                  </div>
                  <select value={x.businessCategory || ""} onChange={(e) => setSelCategory(x.categoryId, e.target.value)} data-testid={`order-cat-${x.categoryId}`}
                    className="w-full mt-1.5 border border-slate-300 px-2 py-1.5 text-xs bg-white focus:ring-2 focus:ring-navy-900 focus:outline-none">
                    <option value="">Select business category…</option>
                    {catList.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    <option value="__other__">Other…</option>
                  </select>
                  {x.businessCategory === "__other__" && (
                    <input autoFocus placeholder="Type your business type" data-testid={`order-cat-other-${x.categoryId}`}
                      onChange={(e) => setSelCategory(x.categoryId, e.target.value)} value=""
                      className="w-full mt-1.5 border border-slate-300 px-2 py-1.5 text-xs focus:ring-2 focus:ring-navy-900 focus:outline-none"
                      onBlur={(e) => { if (!e.target.value) setSelCategory(x.categoryId, ""); }} />
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-200">
            <span className="text-sm uppercase tracking-wider font-bold text-slate-500">Total</span>
            <span className="font-display text-3xl text-emerald-700" data-testid="checkout-total">${total.toFixed(0)}</span>
          </div>

          <div className="mt-6 space-y-3">
            <input placeholder="Business name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="co-business" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <textarea placeholder="Offer details (e.g. 20% off any entree)" value={form.offer_details} onChange={(e) => setForm({ ...form, offer_details: e.target.value })} data-testid="co-offer" rows={2} className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Contact name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="co-name" className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
              <input placeholder="Phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="co-phone" className="border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            </div>
            <input placeholder="Contact email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="co-email" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 mb-1"><Ticket size={13} /> Redemption cap (0 = unlimited)</label>
              <input type="number" min="0" placeholder="e.g. 100" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="co-cap" className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <UploadField label="Artwork / Logo" done={!!logoId} busy={uploading === "Logo"} testid="upload-logo" onFile={(f) => upload(f, setLogoId, "Logo")} />
              <UploadField label="Coupon / Proof" done={!!couponId} busy={uploading === "Coupon"} testid="upload-coupon" onFile={(f) => upload(f, setCouponId, "Coupon")} />
            </div>
            <p className="text-[10px] text-slate-400">Accepted: PDF, PNG, JPG, EPS · up to 15MB</p>
          </div>

          <button onClick={checkout} disabled={checkingOut || !selList.length || !form.business_name || !form.offer_details || !form.contact_email} data-testid="slots-checkout-btn"
            className="w-full mt-6 bg-navy-900 text-white py-4 font-[600] hover:bg-emerald-700 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:translate-y-0">
            {checkingOut ? "Redirecting to Stripe…" : `Checkout · $${total.toFixed(0)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadField({ label, done, busy, onFile, testid }) {
  return (
    <label data-testid={testid} className={`border border-dashed px-3 py-3 text-xs font-[600] cursor-pointer flex flex-col items-center gap-1 transition-colors ${done ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-500 hover:border-navy-900"}`}>
      {done ? <CheckCircle size={18} weight="fill" /> : <UploadSimple size={18} />}
      {busy ? "Uploading…" : done ? `${label} ✓` : label}
      <input type="file" accept="image/*,.pdf,.eps,application/pdf,application/postscript" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
    </label>
  );
}

function AnalyticsTab({ campaigns }) {
  const [cid, setCid] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => { if (campaigns.length) { setCid(campaigns[0].id); } }, [campaigns]);
  useEffect(() => { if (cid) api.get(`/analytics/campaign/${cid}`).then(({ data }) => setRows(data)); }, [cid]);

  const totalViews = rows.reduce((s, r) => s + r.total_views, 0);
  const totalRedemptions = rows.reduce((s, r) => s + r.total_redemptions, 0);
  const rate = totalViews ? ((totalRedemptions / totalViews) * 100).toFixed(1) : "0.0";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Real-time Performance</p>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-1">Merchant Analytics</h2>
        </div>
        <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="analytics-campaign-select" className="border border-slate-300 px-4 py-2.5 bg-white focus:ring-2 focus:ring-navy-900 focus:outline-none">
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>

      <div className="grid sm:grid-cols-3 gap-6 mb-10">
        <Stat icon={Eye} label="Total Views" value={totalViews} color="text-navy-900" />
        <Stat icon={Ticket} label="Total Redemptions" value={totalRedemptions} color="text-emerald-700" />
        <Stat icon={ChartLineUp} label="Redemption Rate" value={`${rate}%`} color="text-amber-600" />
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-6 py-3">Merchant</th><th className="px-6 py-3">Slot</th><th className="px-6 py-3 text-right">Views</th><th className="px-6 py-3 text-right">Redemptions</th></tr>
          </thead>
          <tbody data-testid="analytics-table">
            {rows.length === 0 ? (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400">No active merchants in this campaign yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.slot_id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-4 font-[600]">{r.business_name}</td>
                <td className="px-6 py-4 text-slate-500">{r.face} · {r.slot_number}</td>
                <td className="px-6 py-4 text-right">{r.total_views}</td>
                <td className="px-6 py-4 text-right text-emerald-700 font-[600]">{r.total_redemptions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TrendsSection cid={cid} />
    </div>
  );
}

function TrendChart({ series }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  const w = 240, h = 64, n = series.length, gap = 2;
  const bw = (w - gap * (n - 1)) / n;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-16" data-testid="trend-chart">
      {series.map((s, i) => {
        const bh = Math.max(1, (s.count / max) * (h - 6));
        return (
          <rect key={i} x={i * (bw + gap)} y={h - bh} width={bw} height={bh} rx="1"
            className={s.count > 0 ? "fill-emerald-600" : "fill-slate-200"}>
            <title>{s.date}: {s.count} redemption{s.count === 1 ? "" : "s"}</title>
          </rect>
        );
      })}
    </svg>
  );
}

function TrendsSection({ cid }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (cid) api.get(`/analytics/trends/${cid}?days=14`).then(({ data }) => setData(data)).catch(() => {}); }, [cid]);
  if (!data) return null;
  return (
    <div className="mt-14" data-testid="trends-section">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 flex items-center gap-1.5"><TrendUp size={14} weight="bold" /> Momentum</p>
      <h3 className="font-display text-2xl sm:text-3xl tracking-tight mt-1">Redemption Trends</h3>
      <p className="text-sm text-slate-500 mt-2">Daily redemptions per offer over the last {data.days} days.</p>
      {data.offers.length === 0 ? (
        <div className="mt-6 bg-slate-100 border border-slate-200 p-10 text-center text-slate-400">No active offers to chart yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {data.offers.map((o) => (
            <div key={o.slot_id} className="bg-white border border-slate-200 p-5" data-testid={`trend-card-${o.slot_id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-[700] text-sm text-navy-900 line-clamp-1">{o.business_name}</p>
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400 shrink-0">{o.face[0]}{o.slot_number}</span>
              </div>
              <p className="text-xs text-emerald-700 font-[600] mt-0.5">{o.total} redemptions · {data.days}d</p>
              <div className="mt-3"><TrendChart series={o.series} /></div>
              <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                <span>{data.dates[0].slice(5)}</span>
                <span>{data.dates[data.dates.length - 1].slice(5)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 p-6">
      <Icon size={24} weight="duotone" className="text-slate-400" />
      <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mt-4">{label}</p>
      <p className={`font-display text-4xl tracking-tight mt-1 ${color}`}>{value}</p>
    </div>
  );
}

function ScanTab() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState("");
  const [history, setHistory] = useState([]);
  const scannerRef = useRef(null);

  const parseUrl = (text) => {
    try { const u = new URL(text); const parts = u.pathname.split("/").filter(Boolean); return { id: parts[parts.length - 1], token: u.searchParams.get("t") }; }
    catch { return { id: String(text).trim(), token: null }; }
  };

  const loadHistory = () => api.get("/scan-history").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const exportCsv = () => {
    fetch(`${API}/scan-history/export.csv`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((blob) => { const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "scan-history.csv"; a.click(); URL.revokeObjectURL(url); })
      .catch(() => toast.error("Export failed — please sign in as a merchant."));
  };

  const lookup = async (slotId, token) => {
    try { const { data } = await api.get(`/analytics/slot/${slotId}`); setResult({ ...data, slot_id: slotId, token }); }
    catch { toast.error("Coupon not found for that code."); }
  };

  const stop = async () => {
    try { if (scannerRef.current) { await scannerRef.current.stop(); await scannerRef.current.clear(); } } catch {}
    scannerRef.current = null; setScanning(false);
  };

  const start = async () => {
    setResult(null); setScanning(true);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 }, async (decoded) => {
        const { id, token } = parseUrl(decoded);
        await stop();
        lookup(id, token);
      }, () => {});
    } catch (e) {
      toast.error("Could not open camera. Use manual entry below.");
      setScanning(false);
    }
  };

  useEffect(() => () => { stop(); }, []);

  const confirmRedeem = async () => {
    try {
      let token = result.token;
      if (!token) { const { data: t } = await api.get(`/slots/${result.slot_id}/redeem-token`); token = t.token; }
      const { data } = await api.post(`/slots/${result.slot_id}/redeem`, { token, source: "scan" });
      toast.success("Redemption validated!");
      setResult((r) => ({ ...r, total_redemptions: data.total_redemptions, redemption_cap: data.redemption_cap, closed: data.closed, done: true, token: null }));
      loadHistory();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const slot = result?.slot;
  const cap = slot?.redemption_cap || 0;
  const reached = cap > 0 && (result?.total_redemptions || 0) >= cap;

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">In-Store Validation</p>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-1 mb-2">Scan to Redeem</h2>
        <p className="text-sm text-slate-500 mb-6 max-w-md leading-relaxed">Point your camera at a customer's coupon QR code to instantly pull up and validate their offer.</p>

        <div className="bg-navy-900 border border-slate-200 overflow-hidden aspect-square max-w-sm flex items-center justify-center">
          <div id="qr-reader" className="w-full" data-testid="qr-reader" />
          {!scanning && <div className="text-slate-400 text-sm flex flex-col items-center gap-2"><Camera size={40} weight="duotone" /> Camera is off</div>}
        </div>

        <div className="flex gap-3 mt-4 max-w-sm">
          {!scanning ? (
            <button onClick={start} data-testid="scan-start-btn" className="flex-1 bg-navy-900 text-white py-3 font-[600] hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"><Camera size={18} /> Start Scanner</button>
          ) : (
            <button onClick={stop} data-testid="scan-stop-btn" className="flex-1 border border-slate-300 py-3 font-[600] hover:border-navy-900 transition-colors flex items-center justify-center gap-2"><X size={18} /> Stop</button>
          )}
        </div>

        <div className="mt-6 max-w-sm">
          <label htmlFor="scan-manual-input" className="text-[11px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Or paste a coupon link / slot ID</label>
          <div className="flex gap-2">
            <input id="scan-manual-input" value={manual} onChange={(e) => setManual(e.target.value)} data-testid="scan-manual-input" placeholder="https://…/validate/…" className="flex-1 border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-navy-900 focus:outline-none" />
            <button onClick={() => { if (manual) { const p = parseUrl(manual); lookup(p.id, p.token); } }} data-testid="scan-manual-btn" className="bg-navy-900 text-white px-4 text-sm font-[600] hover:bg-emerald-700 transition-colors">Look up</button>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {result ? (
          <div className="bg-white border border-slate-200 p-8 shadow-sm" data-testid="scan-result">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-sm bg-navy-900 text-white flex items-center justify-center shrink-0"><Storefront size={22} weight="duotone" /></span>
              <h3 className="font-display text-2xl tracking-tight">{slot?.business_name}</h3>
            </div>
            <p className="text-emerald-800 font-[600] mt-4">{slot?.offer_details}</p>
            <div className="mt-6 grid grid-cols-2 gap-px bg-slate-200 border border-slate-200">
              <div className="bg-white p-4"><p className="text-xs uppercase tracking-wider font-bold text-slate-500">Slot</p><p className="font-display text-2xl text-navy-900">{slot?.face} · {slot?.slot_number}</p></div>
              <div className="bg-white p-4"><p className="text-xs uppercase tracking-wider font-bold text-slate-500">Redemptions</p><p className="font-display text-2xl text-emerald-700" data-testid="scan-redemptions">{result.total_redemptions}{cap ? ` / ${cap}` : ""}</p></div>
            </div>
            {result.done ? (
              <div className="mt-6 flex items-center gap-2 text-emerald-700 font-[600]"><CheckCircle size={22} weight="fill" /> {result.closed ? "Validated — offer now closed (limit reached)" : "Redemption validated"}</div>
            ) : (
              <button onClick={confirmRedeem} disabled={reached} data-testid="scan-confirm-btn"
                className={`w-full mt-6 py-4 font-[600] transition-colors duration-200 flex items-center justify-center gap-2 ${reached ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-navy-900 text-white hover:bg-emerald-700"}`}>
                <Ticket size={18} /> {reached ? "Limit Reached — Closed" : "Confirm Redemption"}
              </button>
            )}
          </div>
        ) : (
          <div className="bg-slate-100 border border-slate-200 p-8 flex flex-col items-center justify-center text-center min-h-[220px]">
            <QrCode size={44} weight="duotone" className="text-slate-400" />
            <p className="text-slate-500 mt-4 max-w-xs">Scan a coupon or paste its link to see the offer and validate it here.</p>
          </div>
        )}

        <div className="bg-white border border-slate-200 shadow-sm" data-testid="scan-history">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h4 className="font-display text-lg tracking-tight">Today's Validations</h4>
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-wider font-bold text-slate-400">{history.length}</span>
              <button onClick={exportCsv} data-testid="scan-export-btn" className="text-xs font-[600] text-navy-900 hover:text-emerald-700 flex items-center gap-1"><DownloadSimple size={14} /> CSV</button>
            </div>
          </div>
          {history.length === 0 ? (
            <p className="px-6 py-8 text-center text-slate-400 text-sm">No redemptions validated yet today.</p>
          ) : (
            <ul className="max-h-72 overflow-auto divide-y divide-slate-100">
              {history.map((h) => (
                <li key={h.id} className="px-6 py-3 flex items-center justify-between text-sm" data-testid={`history-row-${h.id}`}>
                  <div>
                    <p className="font-[600] text-navy-900">{h.merchant_name || "—"}</p>
                    <p className="text-xs text-slate-400">{h.face} · {h.slot_number} · {h.source || "consumer"}</p>
                  </div>
                  <span className="text-xs text-slate-500 tabular-nums">{new Date(h.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function AiTab({ aiImg, user }) {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/ai-packages").then(({ data }) => setPackages(data)); }, []);
  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const chosen = packages.filter((p) => selected.includes(p.id));
  const total = chosen.reduce((s, p) => s + (p.setup_price ?? p.price ?? 0), 0);
  const monthly = chosen.reduce((s, p) => s + (p.monthly_price ?? 0), 0);
  const isSub = monthly > 0;
  const dueToday = total + monthly;

  const checkout = async () => {
    if (!selected.length) { toast.error("Toggle at least one package"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout/ai", {
        package_ids: selected, origin_url: window.location.origin,
        business_name: user?.name || "", contact_email: user?.email || "",
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Add-on Automation</p>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-1 mb-6">PMI AI Agent Packages</h2>
        <div className="space-y-4">
          {packages.map((p) => {
            const on = selected.includes(p.id);
            return (
              <motion.div key={p.id} whileHover={{ y: -2 }} data-testid={`ai-package-${p.id}`}
                className={`border p-6 flex items-start justify-between gap-6 bg-white transition-colors ${on ? "border-amber-600 bg-amber-50/40" : "border-slate-200"}`}>
                <div>
                  <div className="flex items-center gap-3">
                    <Robot size={22} weight="duotone" className="text-navy-900" />
                    <h3 className="font-display text-xl tracking-tight">{p.name}</h3>
                  </div>
                  <p className="text-sm text-slate-500 mt-2 max-w-md leading-relaxed">{p.desc}</p>
                  <p className="font-display text-2xl text-emerald-700 mt-3" data-testid={`ai-price-${p.id}`}>
                    ${p.setup_price ?? p.price}<span className="text-sm text-slate-400 font-sans"> setup</span>
                    <span className="text-slate-300 font-sans mx-2">+</span>
                    <span className="text-navy-900">${p.monthly_price ?? 0}<span className="text-sm text-slate-400 font-sans">/mo</span></span>
                  </p>
                </div>
                <button onClick={() => toggle(p.id)} data-testid={`ai-toggle-${p.id}`} role="switch" aria-checked={on}
                  className={`relative w-14 h-8 shrink-0 rounded-full transition-colors duration-200 ${on ? "bg-emerald-700" : "bg-slate-300"}`}>
                  <span className={`absolute top-1 h-6 w-6 bg-white rounded-full transition-transform duration-200 ${on ? "translate-x-7" : "translate-x-1"}`} />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-24">
          <div className="relative h-40 overflow-hidden mb-4">
            <img src={aiImg} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-navy-900/30" />
          </div>
          <div className="bg-white border border-slate-200 p-6 shadow-sm">
            <h3 className="font-display text-2xl tracking-tight">AI Proposal</h3>
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-200">
              <span className="text-sm uppercase tracking-wider font-bold text-slate-500">Total Setup</span>
              <span className="font-display text-3xl text-emerald-700" data-testid="ai-total">${total.toFixed(0)}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm uppercase tracking-wider font-bold text-slate-500">Monthly Recurring</span>
              <span className="font-display text-2xl text-navy-900" data-testid="ai-monthly-total">${monthly.toFixed(0)}<span className="text-sm text-slate-400 font-sans">/mo</span></span>
            </div>
            {isSub && (
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-dashed border-slate-300">
                <span className="text-sm uppercase tracking-wider font-bold text-navy-900">Due Today</span>
                <span className="font-display text-3xl text-emerald-700" data-testid="ai-due-today">${dueToday.toFixed(0)}</span>
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-3">{isSub ? `Charged today: $${dueToday.toFixed(0)} (setup + first month). You'll then be billed $${monthly.toFixed(0)}/mo automatically until cancelled.` : "One-time setup fee, billed once at checkout."}</p>
            <button onClick={checkout} disabled={loading || !selected.length} data-testid="ai-checkout-btn"
              className="w-full mt-6 bg-navy-900 text-white py-4 font-[600] hover:bg-emerald-700 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:translate-y-0">
              {loading ? "Redirecting…" : isSub ? `Subscribe · $${dueToday.toFixed(0)} today` : `Pay · $${total.toFixed(0)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubscriptionsTab({ user }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/subscriptions").then(({ data }) => setSubs(data)).catch(() => setSubs([])).finally(() => setLoading(false));
  useEffect(() => { if (user) load(); else setLoading(false); /* eslint-disable-next-line */ }, [user]);

  const cancel = async (sid) => {
    if (!window.confirm("Cancel this subscription at the end of the current billing period? You'll keep access until then.")) return;
    setBusy(sid);
    try { await api.post(`/subscriptions/${sid}/cancel`); toast.success("Subscription will cancel at period end"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!user) {
    return (
      <div className="bg-white border border-slate-200 p-12 text-center" data-testid="subs-signin">
        <Receipt size={40} weight="duotone" className="text-slate-300 mx-auto" />
        <p className="text-slate-500 mt-4">Please <Link to="/login" className="text-emerald-700 font-[600] hover:underline">sign in</Link> to view and manage your AI subscriptions.</p>
      </div>
    );
  }

  return (
    <div data-testid="subscriptions-tab">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Billing</p>
      <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-1 mb-6">My Subscriptions</h2>
      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : subs.length === 0 ? (
        <div className="bg-slate-100 border border-slate-200 p-12 text-center text-slate-400">No active AI subscriptions yet. Start one from the AI Services tab.</div>
      ) : (
        <div className="space-y-4">
          {subs.map((s) => {
            const canceling = s.cancel_at_period_end;
            const paid = s.payment_status === "paid";
            const active = paid && !canceling;
            const label = canceling ? "Canceling at period end" : active ? "Active" : paid ? s.subscription_status : "Pending payment";
            const badge = canceling ? "bg-amber-100 text-amber-700" : active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500";
            return (
              <div key={s.session_id} data-testid={`sub-${s.session_id}`} className="bg-white border border-slate-200 p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-[700] text-navy-900">{s.packages.join(", ")}</p>
                  <p className="text-sm text-slate-500 mt-1">${Number(s.monthly_total).toFixed(0)}/mo{s.created_at ? ` · started ${new Date(s.created_at).toLocaleDateString()}` : ""}</p>
                  <span className={`inline-block mt-2 text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 ${badge}`} data-testid={`sub-status-${s.session_id}`}>{label}</span>
                </div>
                {active && (
                  <button onClick={() => cancel(s.session_id)} disabled={busy === s.session_id} data-testid={`sub-cancel-${s.session_id}`}
                    className="border border-slate-300 text-slate-600 px-5 py-2.5 text-sm font-[600] hover:border-red-500 hover:text-red-600 transition-colors disabled:opacity-50">
                    {busy === s.session_id ? "Canceling…" : "Cancel"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
